import { useEffect, useEffectEvent, useState } from 'react';

import { useAtomValue } from 'jotai';

import Constants from '@/constants';

import type {
  ContentReference,
  Resource,
  SearchDocument,
} from '@/_generated/api-clients/xp';
import { documentTransientAtom } from '@/atoms/document-transient';

interface PathwayMessage {
  type?: string;
  code?: unknown;
}

export const extractBodyContent = (htmlContent?: string) => {
  if (!htmlContent) {
    return '';
  }
  const parser = new DOMParser();
  const doc = parser.parseFromString(htmlContent, 'text/html');
  const body = doc.querySelector('body');
  return body ? body.innerHTML : '';
};

export const fixTridionImageSources = (
  htmlContent?: string,
): string | undefined => {
  if (!htmlContent) {
    return undefined;
  }
  const baseUrl = Constants.XP_API_IMAGE_BASE_URL;
  const html = new DOMParser().parseFromString(htmlContent, 'text/html');
  const images = html.querySelectorAll('img');

  images.forEach(image => {
    const updatedImageSource = /.*\/(binary\/.*)/.exec(image.src)?.[1];
    if (updatedImageSource !== undefined) {
      image.src = `${baseUrl}/${updatedImageSource}`;
    }
  });
  return html.body.innerHTML;
};

export const contentReferenceToDocument = (
  contentReference: ContentReference,
  documentId: string,
  resource?: Resource,
): SearchDocument => ({
  id: documentId,
  contentApiUrl: contentReference.contentApiUrl,
  contentUrl: contentReference.contentUrl,
  lastModified: contentReference.lastModified,
  name: contentReference.name,
  otherContentUrl: contentReference.otherContentUrl,
  otherName: contentReference.otherName,
  otherPrintUrl: contentReference.otherPrintUrl,
  printUrl: contentReference.printUrl,
  searchId: '',
  sourceSystem: contentReference.sourceSystem,
  tags: contentReference.tags,
  type: resource?.type,
});

export const useDocumentErrorListener = (): unknown => {
  const [errorMessage, setErrorMessage] = useState<unknown>();

  const onMessageReceivedFromContent = useEffectEvent(
    (eventMessage: MessageEvent) => {
      if (eventMessage.origin === Constants.PATHWAY_API_URL) {
        const data: unknown = eventMessage.data;

        // This is a message sent to indicate a successful page load
        if (data === 'ok') {
          return;
        }

        if (
          typeof data === 'object' &&
          data !== null &&
          'type' in data &&
          (data as PathwayMessage).type === 'Error'
        ) {
          setErrorMessage((data as PathwayMessage).code);
        }
      }
    },
  );

  useEffect(() => {
    window.addEventListener('message', onMessageReceivedFromContent);

    return () => {
      window.removeEventListener('message', onMessageReceivedFromContent);
    };
  }, []);

  return errorMessage;
};

export const useWindowScrollEvents = () => {
  useEffect(() => {
    window.updateScrollPosition = (top: unknown) => {
      const scrollEvent = new MessageEvent('iFrameScroll', {
        data: JSON.stringify(top),
      });
      window.dispatchEvent(scrollEvent);
    };
  }, []);
};

const SUPPORTED_LANG_CODES = Object.values(Constants.LANGUAGE);

const isSupportedLang = (lang: string) => SUPPORTED_LANG_CODES.includes(lang);

const extractLangFromUrl = (url?: string) => {
  if (!url) return Constants.LANGUAGE.ENGLISH_LANGUAGE_CODE;

  const parts = url.split('/').filter(Boolean);
  const candidate = parts[parts.length - 2]?.slice(0, 2) ?? '';

  return isSupportedLang(candidate)
    ? candidate
    : Constants.LANGUAGE.ENGLISH_LANGUAGE_CODE;
};

const getPreferredLanguageOptions = (
  document: SearchDocument | undefined,
): {
  primary: string;
  secondary: string;
} => {
  if (!document?.contentUrl)
    return {
      primary: Constants.LANGUAGE.ENGLISH_LANGUAGE_CODE,
      secondary: Constants.LANGUAGE.ENGLISH_LANGUAGE_CODE,
    };

  const primary = extractLangFromUrl(document.contentUrl);
  return {
    primary,
    secondary: document.otherContentUrl
      ? extractLangFromUrl(document.otherContentUrl)
      : primary,
  };
};

interface TridionLocalizedContent {
  url: string | undefined;
  language: string;
  title: string | undefined;
}

/** Get url for a tridion document based on using the translated url or not and send the translated. */
export const useTridionLocalizedContent = (
  document: SearchDocument,
): TridionLocalizedContent => {
  if (!document.contentUrl) {
    const err = new Error('Document does not have a content url.') as Error & {
      statusCode: number;
    };
    err.statusCode = 400;
    throw err;
  }

  const { isTranslating } = useAtomValue(documentTransientAtom);

  const url = isTranslating ? document.otherContentUrl : document.contentUrl;
  const title = isTranslating ? document.otherName : document.name;
  const langPrefCodes = getPreferredLanguageOptions(document);
  const language = isTranslating
    ? langPrefCodes.secondary
    : langPrefCodes.primary;

  return { url, title, language };
};

export const isXpId = (str: string): boolean => str.startsWith('crn:');

export const extractGameUrl = async (contentUrl: string) => {
  const extractHttpsUrl = (text: string): string => {
    if (text.startsWith('https')) return text;

    const index = text.indexOf('https://');
    return index !== -1 ? text.slice(index).trim() : '';
  };

  try {
    const response = await fetch(contentUrl, {
      credentials: 'include',
    });

    const resText = await response.text();
    return extractHttpsUrl(resText);
  } catch {
    console.error('There was an error extracting the document URL');
  }
};

/** Extract game information from HTML content. */
export const extractTridionGameInformation = (htmlContent?: string) => {
  if (!htmlContent) {
    return;
  }
  const html = new DOMParser().parseFromString(htmlContent, 'text/html');
  const description = html.querySelector('.shortdesc');
  return {
    description: description?.textContent,
    url: html.querySelector('iframe')?.src,
  };
};
